import { tickableViews, type ProgressViewKey } from "./progressViews";

/* 🔴 W125: the tick list is `tickableViews` — the offered views less `oneRM`, which is the exercise's own `1RM` switch now
 * (`one_rm_enabled`). A stored `oneRM` (the column's older vocabulary) is ignored here as the phone ignores it.
 *
 * THE COACH'S PROGRESS VIEWS PER EXERCISE — W72 step 3 (his 2026-09-25 addition, `docs/WEB-OURS-PROPOSAL.md`
 * Proposal 3): the views are DERIVED from what the exercise logs (`offeredViews`, generated from the phone's own
 * `ProgressChartPlan.offered`), all on by default, and the coach unticks what makes no sense for this exercise.
 *
 * 🔴 THE STORED SHAPE: `coach_exercises.progress_views` EMPTY = every view offered. So a coach who never touches
 * the row stores nothing, a tick list equal to the offered list is stored as EMPTY (never as a copy that would
 * go stale the day the exercise gains a logged field), and only a real narrowing is written.
 *
 * ⚠️ THE SAME FALL-BACK AS THE PHONE (`ProgressChartPlan.allowed`): ticks that leave nothing offered read as
 * ALL — so the web shows exactly what the phone's menu will do, and the last tick cannot be removed.
 */

/** What the rows show as ticked, for this exercise's logged fields. */
export function tickedViews(stored: readonly string[], trackingFields: readonly string[]): ProgressViewKey[] {
  const offered = tickableViews(trackingFields);
  if (stored.length === 0) return [...offered];
  const narrowed = offered.filter((k) => stored.includes(k));
  return narrowed.length === 0 ? [...offered] : narrowed;
}

/** The column's value for a tick list: EMPTY when it is every view offered. */
export function storedViews(ticked: readonly string[], trackingFields: readonly string[]): ProgressViewKey[] {
  const offered = tickableViews(trackingFields);
  const kept = offered.filter((k) => ticked.includes(k));
  return kept.length === 0 || kept.length === offered.length ? [] : kept;
}

/** Whether this row's box may be changed: the LAST ticked view stays (a menu of none is a dead control). */
export function canToggleView(stored: readonly string[], trackingFields: readonly string[], key: ProgressViewKey): boolean {
  const ticked = tickedViews(stored, trackingFields);
  return !(ticked.length === 1 && ticked[0] === key);
}

/** One box pressed → the new stored value. A refused press returns the stored value unchanged. */
export function toggleView(stored: readonly string[], trackingFields: readonly string[], key: ProgressViewKey): ProgressViewKey[] {
  if (!canToggleView(stored, trackingFields, key)) return storedViews(tickedViews(stored, trackingFields), trackingFields);
  const ticked = tickedViews(stored, trackingFields);
  const next = ticked.includes(key) ? ticked.filter((k) => k !== key) : [...ticked, key];
  return storedViews(next, trackingFields);
}
